import React, { useMemo, useState } from 'react';

const DUMMY_CATEGORIES = [
  {
    id: 'sweets',
    name: 'Sweets',
    items: 353,
    subCategories: [
      { name: 'Khoya sweets', items: 97 },
      { name: 'Kaju sweets', items: 70 },
      { name: 'Ghee sweets', items: 62 },
      { name: 'Bengali sweets', items: 43 },
      { name: 'Lohri special', items: 18 },
      { name: 'Petha sweet', items: 13 },
      { name: 'Ganesh chaturthi special', items: 13 },
      { name: 'Premium', items: 11 },
      { name: 'Holi special', items: 7 },
      { name: 'Sawan special', items: 7 },
      { name: 'Navratri special', items: 6 },
      { name: 'Janmashtami special', items: 4 },
      { name: 'Karwachauth special', items: 2 },
    ],
  },
  {
    id: 'bakery',
    name: 'Bakery',
    items: 60,
    subCategories: [
      { name: 'Biscuit', items: 26 },
      { name: 'Pastry', items: 9 },
      { name: 'Bakery snacks', items: 9 },
      { name: 'Dry cakes', items: 8 },
      { name: 'Pastry cake', items: 7 },
      { name: 'Cookies', items: 1 },
    ],
  },
  {
    id: 'restaurant',
    name: 'Restaurant',
    items: 245,
    subCategories: [
      { name: 'North indian', items: 139 },
      { name: 'South indian', items: 32 },
      { name: 'Beverages', items: 28 },
      { name: 'Chaat', items: 24 },
      { name: 'Indian snacks', items: 16 },
      { name: 'Navratri special', items: 6 },
    ],
  },
  {
    id: 'fmcg',
    name: 'FMCG',
    items: 288,
    subCategories: [
      { name: 'FMCG', items: 96 },
      { name: 'Gifting', items: 89 },
      { name: 'Savouries', items: 79 },
      { name: 'Party hall', items: 13 },
      { name: 'Premium', items: 11 },
    ],
  },
];

function StepConfigureTool({ wizardData, onChange }) {
  const [catSearch, setCatSearch] = useState('');
  const [expandedCat, setExpandedCat] = useState(null);

  const filteredCategories = useMemo(() => {
    const q = catSearch.trim().toLowerCase();
    if (!q) return DUMMY_CATEGORIES;
    return DUMMY_CATEGORIES.filter((c) => c.name.toLowerCase().includes(q));
  }, [catSearch]);

  const selectedCategories = wizardData.categories || [];
  const allSelected = DUMMY_CATEGORIES.length > 0 && selectedCategories.length === DUMMY_CATEGORIES.length;

  const toggleCategory = (id) => {
    const next = selectedCategories.includes(id)
      ? selectedCategories.filter((c) => c !== id)
      : [...selectedCategories, id];
    onChange({ categories: next });
  };

  const toggleAll = () => {
    onChange({
      categories: allSelected ? [] : DUMMY_CATEGORIES.map((c) => c.id),
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Step 2: Configure Tool</h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure categories, items and basic details for your tool.
        </p>
      </div>

      <div className="grid items-stretch gap-6 lg:grid-cols-2">
        {/* Left — Categories */}
        <div className="h-full">
          <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-4">
            <h4 className="mb-3 text-sm font-semibold text-gray-800">Select Categories</h4>
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={catSearch}
                  onChange={(e) => setCatSearch(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-[#F7E5D5] py-1.5 pl-8 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>
              <button
                type="button"
                onClick={toggleAll}
                className="shrink-0 text-xs font-medium text-brand hover:underline"
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <ul className="flex-1 space-y-1 overflow-y-auto">
              {filteredCategories.map((cat) => {
                const checked = selectedCategories.includes(cat.id);
                const isExpanded = expandedCat === cat.id;
                return (
                  <li key={cat.id}>
                    <div className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCategory(cat.id)}
                        className="h-4 w-4 rounded border-gray-300 text-brand accent-brand"
                      />
                      <span
                        className={`flex-1 ${checked ? 'font-medium text-gray-900' : 'text-gray-700'}`}
                        onClick={() => setExpandedCat(isExpanded ? null : cat.id)}
                      >
                        {cat.name}
                      </span>
                      <span className="text-xs text-gray-400">{cat.items} items</span>
                      {cat.subCategories?.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setExpandedCat(isExpanded ? null : cat.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                            className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {isExpanded && cat.subCategories?.length > 0 && (
                      <ul className="ml-8 mt-1 space-y-0.5 border-l border-gray-100 pl-2">
                        {cat.subCategories.map((sub) => (
                          <li key={sub.name} className="flex items-center justify-between rounded px-2 py-1 text-xs text-gray-600">
                            <span>{sub.name}</span>
                            <span className="text-gray-400">{sub.items}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

        </div>

        {/* Right — Tool Details */}
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h4 className="mb-4 text-sm font-semibold text-gray-800">Tool Details</h4>
          <div className="space-y-4">
            <label className="flex flex-col text-sm text-gray-700">
              <span>
                Tool Name <span className="text-red-600">*</span>
              </span>
              <input
                type="text"
                value={wizardData.toolName || ''}
                onChange={(e) => onChange({ toolName: e.target.value })}
                placeholder="Enter tool name"
                className="mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span>
                Tool Code <span className="text-red-600">*</span>
              </span>
              <input
                type="text"
                value={wizardData.toolCode || ''}
                onChange={(e) => onChange({ toolCode: e.target.value })}
                placeholder="e.g. SWT-001"
                className="mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Description (Optional)
              <textarea
                value={wizardData.toolDescription || ''}
                onChange={(e) => onChange({ toolDescription: e.target.value })}
                placeholder="Weekly ordering tool for sweets category items."
                rows={3}
                className="mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span>
                Date <span className="text-red-600">*</span>
              </span>
              <input
                type="date"
                value={wizardData.toolDate || ''}
                onChange={(e) => onChange({ toolDate: e.target.value })}
                className="mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StepConfigureTool;
